import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import { Search, Calendar, Clock, Video, FileText, ChevronLeft, ChevronRight, XCircle } from 'lucide-react';
import { schedulingApi, type AppointmentStatus } from '@/api/http-client';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';

interface AppointmentHistory {
  id: string;
  doctorName: string;
  specialty: string;
  date: string;
  time: string;
  status: 'COMPLETED' | 'CANCELLED' | 'NO_SHOW' | 'IN_PROGRESS';
  type: 'EXPRESS' | 'SCHEDULED';
  price: number;
  medicalRecordId?: string;
  prescriptionId?: string;
  certificateId?: string;
  rating?: number;
}

interface HistoryResponse {
  appointments: AppointmentHistory[];
  total: number;
  page: number;
  pageSize: number;
  stats: {
    totalConsultations: number;
    totalSpent: number;
    averageRating: number;
  };
}

export function HistoryPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'ALL' | 'COMPLETED' | 'CANCELLED'>('ALL');

  const { data, isLoading, isError, refetch } = useQuery<HistoryResponse>({
    queryKey: ['patient', 'history', 'stats'],
    queryFn: async () => {
      const statusFilter = filter === 'ALL' ? undefined : filter === 'COMPLETED' 
        ? ['FINISHED'] as AppointmentStatus[] 
        : ['CANCELLED', 'NO_SHOW'] as AppointmentStatus[];
      
      const response = await schedulingApi.getPatientAppointments({
        status: statusFilter,
        limit: 100,
      });

      const appointments: AppointmentHistory[] = response.appointments.map((apt) => {
        const isExpress = apt.type === 'EXPRESS' || apt.type === 'EMERGENCY';
        const isFinished = apt.status === 'FINISHED';
        const isCancelled = apt.status === 'CANCELLED';
        const isNoShow = apt.status === 'NO_SHOW';
        const isInProgress = apt.status === 'IN_PROGRESS' || apt.status === 'DOCTOR_ACCEPTED' || apt.status === 'WAITING_DOCTOR' || apt.status === 'PAID';

        let resolvedStatus: 'COMPLETED' | 'CANCELLED' | 'NO_SHOW' | 'IN_PROGRESS';
        if (isFinished) {
          resolvedStatus = 'COMPLETED';
        } else if (isCancelled) {
          resolvedStatus = 'CANCELLED';
        } else if (isNoShow) {
          resolvedStatus = 'NO_SHOW';
        } else if (isInProgress) {
          resolvedStatus = 'IN_PROGRESS';
        } else {
          resolvedStatus = 'CANCELLED';
        }

        return {
          id: apt.id,
          doctorName: apt.doctor?.name || (isExpress ? 'Dr(a). Plantonista' : 'Médico Medikei'),
          specialty: apt.doctor?.specialty || (isExpress ? 'Clínica Geral' : 'Consulta Médica'),
          date: apt.scheduledAt,
          time: new Date(apt.scheduledAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
          status: resolvedStatus,
          type: isExpress ? ('EXPRESS' as const) : ('SCHEDULED' as const),
          price: apt.price,
        };
      });

      const completedAppointments = appointments.filter(a => a.status === 'COMPLETED' || a.status === 'IN_PROGRESS');
      const totalSpent = completedAppointments.reduce((sum, apt) => sum + apt.price, 0);

      return {
        appointments,
        total: response.pagination.total,
        page,
        pageSize: 10,
        stats: {
          totalConsultations: completedAppointments.length,
          totalSpent,
          averageRating: 0,
        },
      };
    },
  });

  const filteredAppointments = data?.appointments.filter((apt) => {
    const matchesSearch = apt.doctorName.toLowerCase().includes(search.toLowerCase()) ||
                          apt.specialty.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'ALL' || apt.status === filter;
    return matchesSearch && matchesFilter;
  }) || [];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Histórico de Consultas</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total de Consultas</p>
              <p className="text-2xl font-bold">{data?.stats.totalConsultations}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Gasto</p>
              <p className="text-2xl font-bold">{formatCurrency(data?.stats.totalSpent || 0)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <span className="text-2xl">⭐</span>
            </div>
            <div>
              <p className="text-sm text-gray-500">Avaliação Média</p>
              <p className="text-2xl font-bold">{data?.stats.averageRating?.toFixed(1)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por médico ou especialidade..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="ALL">Todos Status</option>
          <option value="COMPLETED">Concluídas</option>
          <option value="CANCELLED">Canceladas</option>
        </select>
      </div>

      {isLoading ? (
        <LoadingOverlay message="Carregando histórico..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar histórico" onRetry={() => refetch()} />
      ) : filteredAppointments.length === 0 ? (
        <EmptyState
          icon="FileText"
          title="Nenhuma consulta no histórico"
          description={search || filter !== 'ALL' ? 'Tente ajustar os filtros' : 'Suas consultas aparecerão aqui após serem realizadas'}
          action={{ label: 'Atualizar', onClick: () => refetch() }}
        />
      ) : (
        <div className="space-y-4">
          {filteredAppointments.map((apt) => (
            <Card key={apt.id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${
                      apt.status === 'COMPLETED'
                        ? 'bg-green-100 dark:bg-green-950/40'
                        : apt.status === 'IN_PROGRESS'
                        ? 'bg-blue-100 dark:bg-blue-950/40'
                        : 'bg-red-100 dark:bg-red-950/40'
                    }`}>
                      {apt.status === 'COMPLETED' || apt.status === 'IN_PROGRESS' ? (
                        <Video className={`h-6 w-6 ${apt.status === 'COMPLETED' ? 'text-green-600' : 'text-blue-600'}`} />
                      ) : (
                        <XCircle className="h-6 w-6 text-red-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{apt.doctorName}</h3>
                      <p className="text-sm text-gray-500">{apt.specialty}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(apt.date)}</span>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="h-4 w-4" />
                          <span>{apt.time}</span>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          apt.type === 'EXPRESS' ? 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                        }`}>
                          {apt.type === 'EXPRESS' ? 'Expressa' : 'Agendada'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                      apt.status === 'COMPLETED'
                        ? 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300'
                        : apt.status === 'IN_PROGRESS'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                        : apt.status === 'NO_SHOW'
                        ? 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                        : 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                    }`}>
                      {apt.status === 'COMPLETED' ? 'Concluída' : apt.status === 'IN_PROGRESS' ? 'Em Andamento' : apt.status === 'NO_SHOW' ? 'Não Compareceu' : 'Cancelada'}
                    </span>
                    <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(apt.price)}</span>
                  </div>
                </div>

                {apt.status === 'COMPLETED' && (
                  <div className="mt-4 pt-4 border-t flex items-center gap-2">
                    {apt.medicalRecordId && (
                      <button
                        onClick={() => navigate(`/medical-records/${apt.medicalRecordId}`)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                      >
                        <FileText className="h-4 w-4" />
                        Ver Prontuário
                      </button>
                    )}
                    {apt.prescriptionId && (
                      <button
                        onClick={() => navigate(`/prescriptions/${apt.prescriptionId}`)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100"
                      >
                        Ver Receita
                      </button>
                    )}
                    {apt.certificateId && (
                      <button
                        onClick={() => navigate(`/certificates/${apt.certificateId}`)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100"
                      >
                        Ver Atestado
                      </button>
                    )}
                    {apt.rating && (
                      <div className="ml-auto flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            className={`text-lg ${star <= apt.rating! ? 'text-yellow-400' : 'text-gray-300'}`}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
          className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="px-4 py-2 bg-blue-600 text-white rounded-lg">{page}</span>
        <button
          onClick={() => setPage(page + 1)}
          disabled={page * 10 >= (data?.total || 0)}
          className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
